import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#menu', label: t('nav.menu') },
    { href: '#gallery', label: t('nav.gallery') },
    { href: '#reviews', label: t('nav.reviews') },
    { href: '#contact', label: t('nav.contact') }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#1A1816]/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
            className="flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            data-testid="logo"
          >
            <span className="font-playfair text-2xl md:text-3xl font-bold text-[#9F3E1F]">
              AK-AL
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <motion.a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="font-manrope text-[#FDFBF7] hover:text-[#9F3E1F] transition-colors font-medium"
                whileHover={{ y: -2 }}
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </motion.a>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-[#2D2A26] rounded-full p-1" data-testid="language-switcher">
              <button
                onClick={() => setLanguage('nl')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  language === 'nl' 
                    ? 'bg-[#9F3E1F] text-white' 
                    : 'text-[#FDFBF7] hover:bg-[#FDFBF7]/10'
                }`}
                data-testid="lang-nl"
              >
                <span className="mr-1">🇳🇱</span>
                NL
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-[#9F3E1F] text-white' 
                    : 'text-[#FDFBF7] hover:bg-[#FDFBF7]/10'
                }`}
                data-testid="lang-en"
              >
                <span className="mr-1">🇬🇧</span>
                EN
              </button>
            </div>

            {/* Call Button - Desktop */}
            <motion.a
              href="tel:0172493231"
              className="hidden md:flex items-center gap-2 bg-[#9F3E1F] text-white px-4 py-2 rounded-full font-manrope font-medium hover:bg-[#8A351A] transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="call-button"
            >
              <Phone size={18} />
              <span>0172 493 231</span>
            </motion.a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[#FDFBF7]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#1A1816] border-t border-[#2D2A26]"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="block py-2 font-manrope text-[#FDFBF7] hover:text-[#9F3E1F] transition-colors font-medium"
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:0172493231"
                className="flex items-center gap-2 bg-[#9F3E1F] text-white px-4 py-3 rounded-full font-manrope font-medium justify-center mt-4"
                data-testid="mobile-call-button"
              >
                <Phone size={18} />
                <span>{t('contact.callUs')}: 0172 493 231</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
